import { AlulaService, AlulaUserService } from "@app/project/services/alula.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, map, mergeMap, switchMap } from "rxjs/operators";
import { AlulaActions, DataActions, ProjectActions } from "../actions";

import { Injectable } from "@angular/core";
import { AlulaDeviceDbService } from "@app/project/services/alula-device-db.service";
import { AlulaEventService } from "@app/project/services/alula-event.service";
import { Action } from "@ngrx/store";
import { Observable } from "rxjs";
import { ActionWord } from "../../../shared/resources/action-word";

const alulaActionTypes = AlulaActions.ActionTypes;
type AlulaUserAction = AlulaActions.AlulaUserAction;

@Injectable()
export class AlulaEffects {
    constructor(
        private actions$: Actions,
        private alulaService: AlulaService,
        private alulaDeviceDbService: AlulaDeviceDbService,
        private alulaEventService: AlulaEventService,
        private alulaUserService: AlulaUserService
    ) {}

    createAlulaUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.CreateAlulaUser),
            switchMap((action: AlulaActions.CreateAlulaUser) =>
                DataActions.toAction()(
                    this.alulaUserService.createAlulaUser(action.payload.user, action.payload.device),
                    AlulaActions.CreateAlulaUserSuccess,
                    AlulaActions.CreateAlulaUserFailure
                )
            ),
            catchError(DataActions.toError(AlulaActions.GetAlulaUserFailure))
        )
    );

    createAlulaUserFailure$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(ofType(alulaActionTypes.CreateAlulaUserFailure), map(DataActions.handleErrorMessage(ActionWord.created)))
    );

    getAlulaUserByPartialUsername: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.GetAlulaUserByPartialUsername),
            mergeMap((action: AlulaActions.GetAlulaUserByPartialUsername) =>
                DataActions.toAction()(
                    this.alulaUserService.getUserByPartialUsername(action.payload.partialUsername),
                    AlulaActions.GetAlulaUserSuccess,
                    AlulaActions.GetAlulaUserFailure
                )
            ),
            catchError(
                DataActions.toCustomErrorAction(
                    AlulaActions.GetAlulaUserFailure,
                    "Error communicating with the Clare security server. -100"
                )
            )
        )
    );

    getAlulaUserFailure: Observable<Action> = createEffect(() =>
        this.actions$.pipe(ofType(alulaActionTypes.GetAlulaUserFailure), map(DataActions.handleErrorMessage()))
    );

    getAlulaUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.GetAlulaUser),
            mergeMap((action: AlulaUserAction) =>
                DataActions.toAction()(
                    this.alulaUserService.getUserByUTypeAndEmail(action.payload),
                    AlulaActions.GetAlulaUserSuccess,
                    AlulaActions.GetAlulaUserFailure
                )
            ),
            catchError(DataActions.toError(AlulaActions.GetAlulaUserFailure))
        )
    );

    getAlulaDevice$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.GetAlulaDevice),
            mergeMap((action: AlulaActions.GetAlulaDevice) =>
                DataActions.toAction()(
                    this.alulaDeviceDbService.getDeviceByMacAddress(action.payload),
                    AlulaActions.GetAlulaDeviceSuccess,
                    AlulaActions.GetAlulaDeviceFailure
                )
            ),
            catchError(DataActions.toError(AlulaActions.GetAlulaDeviceFailure))
        )
    );

    getAlulaDeviceEvents$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.GetAlulaDeviceEvents),
            switchMap((action: AlulaActions.GetAlulaDeviceEvents) => {
                const { deviceId, filters = {} } = action.payload;

                return DataActions.toAction()(
                    this.alulaEventService.getEventsByDeviceId(deviceId, filters),
                    AlulaActions.GetAlulaDeviceEventsSuccess,
                    AlulaActions.GetAlulaDeviceEventsFailure
                );
            }),
            catchError(DataActions.toError(AlulaActions.GetAlulaDeviceEventsFailure))
        )
    );

    getDevicesByReceiverGroup$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.GetDevicesByReceiverGroupId),
            mergeMap((action: AlulaActions.GetDevicesByReceiverGroupId) =>
                DataActions.toAction()(
                    this.alulaDeviceDbService.getDevicesByReceiverGroupId(action.payload.receiverGroupId),
                    AlulaActions.GetDevicesByReceiverGroupIdSuccess,
                    AlulaActions.GetDevicesByReceiverGroupIdFailure
                )
            ),
            catchError(DataActions.toError(AlulaActions.GetDevicesByReceiverGroupIdFailure))
        )
    );

    getDevicesByDealerId$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.GetDevicesByDealerId),
            mergeMap((action: AlulaActions.GetDevicesByDealerId) =>
                DataActions.toAction()(
                    this.alulaDeviceDbService.getARNDevices(action.payload.dealerId),
                    AlulaActions.GetDevicesByDealerIdSuccess,
                    AlulaActions.GetDevicesByDealerIdFailure
                )
            ),
            catchError(DataActions.toError(AlulaActions.GetDevicesByReceiverGroupIdFailure))
        )
    );

    updateAlulaDevice$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.UpdateAlulaDevice),
            concatMap((action: AlulaActions.UpdateAlulaDevice) =>
                DataActions.toAction()(
                    this.alulaService.updateAlulaDevice(action.payload),
                    AlulaActions.UpdateAlulaDeviceSuccess,
                    AlulaActions.UpdateAlulaDeviceFailure
                )
            ),
            catchError(DataActions.toError(AlulaActions.UpdateAlulaDeviceFailure))
        )
    );

    updateAlulaPropertiesDevice$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.UpdateAlulaDeviceReceiver),
            concatMap((action: AlulaActions.UpdateAlulaDeviceReceiver) =>
                DataActions.toAction(new AlulaActions.ClearDevicesByDealerId(), new ProjectActions.ClearCentralStationProjectData())(
                    this.alulaDeviceDbService.updateSelectAlulaDeviceProperties(action.payload),
                    AlulaActions.UpdateAlulaDeviceReceiverSuccess,
                    AlulaActions.UpdateAlulaDeviceReceiverFailure,
                    action.callbackEmitter
                )
            ),
            catchError(DataActions.toError(AlulaActions.UpdateAlulaDeviceFailure))
        )
    );

    updateAlulaDeviceSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.UpdateAlulaDeviceSuccess, alulaActionTypes.UpdateAlulaDeviceReceiverSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.saved, null, "device"))
        )
    );

    updateAlulaDeviceFailure: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.UpdateAlulaDeviceFailure, alulaActionTypes.UpdateAlulaDeviceReceiverFailure),
            map((action: AlulaActions.UpdateAlulaDeviceFailure) => {
                if (action?.error?.error?.code !== 0 && action?.error?.error?.message) {
                    action.error.error = action.error.error.message;
                }
                return action;
            }),
            map(DataActions.handleErrorMessage(ActionWord.saved, "device"))
        )
    );

    // assign the device

    enableSecurityMonitoring$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.EnableSecurityMonitoring),
            concatMap((action: AlulaActions.EnableSecurityMonitoring) =>
                DataActions.toAction()(
                    this.alulaService.enableSecurityMonitoring(action.payload).pipe(
                        map(device => ({
                            ...action.payload,
                            device,
                        }))
                    ),
                    AlulaActions.EnableSecurityMonitoringSuccess,
                    AlulaActions.EnableSecurityMonitoringFailure,
                    action.payload.emitter
                )
            ),
            catchError(DataActions.toError(AlulaActions.EnableSecurityMonitoringFailure))
        )
    );

    private readonly securityMonitoringText = "security monitoring services";

    enableSecurityMonitoringSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.EnableSecurityMonitoringSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.enabled, null, this.securityMonitoringText))
        )
    );

    enableSecurityMonitoringFailure: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.EnableSecurityMonitoringFailure),
            map((action: AlulaActions.EnableSecurityMonitoringFailure) => {
                if (action.error && action.error.error.code !== 0 && action.error.error.message) {
                    // here to bubble up CRC MAC mismatch error
                    action.error.error = action.error.error.message;
                }
                return action;
            }),
            map(DataActions.handleErrorMessage(ActionWord.enabled, this.securityMonitoringText))
        )
    );

    disableSecurityMonitoring$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.DisableSecurityMonitoring),
            concatMap((action: AlulaActions.DisableSecurityMonitoring) =>
                DataActions.toAction()(
                    this.alulaService.disableSecurityMonitoring(action.payload.device, action.payload),
                    AlulaActions.DisableSecurityMonitoringSuccess,
                    AlulaActions.DisableSecurityMonitoringFailure,
                    action.payload.emitter
                )
            ),
            catchError(DataActions.toError(AlulaActions.DisableSecurityMonitoringFailure))
        )
    );

    disableSecurityMonitoringSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.DisableSecurityMonitoringSuccess),
            map(DataActions.handleSuccessMessage(ActionWord.disabled, null, this.securityMonitoringText))
        )
    );

    disableSecurityMonitoringFailure: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(alulaActionTypes.DisableSecurityMonitoringFailure),
            map(DataActions.handleErrorMessage(ActionWord.disabled, this.securityMonitoringText))
        )
    );

    // if the panel is assigned and its customerId matches the customer's ID

    // the panel is not assigned, get the username based on the mac address
    // what to do if the customer info needs to be updated?????
    // is it a security risk to just update it
    // } else if (!payload.user.id || ) {

    // if (the panel is assigned) {
    // get the device's customerId
    // if (the id of the customer === the customer assigned to the device) {
    //     update the customer
    // } else {
    //     the customer doesn't exist or it exists but its ID doesn't match the customer ID of the panel
    // }
    // } else if (the user doesn't exist || the user's email doesn't equal the submitted email) {
    // create user with first name, last name and email
    // }

    // update the device with the properties

    // if (device property update successful && the panel is not assigned) {
    //     if the device does not have a customer ID
    //    if (payload.device.customerId) {
    //         assign the panel
    //        return new ProjectActions.AssignAlulaDevice({ customerId: payload.customer.id, deviceId: payload.device.id });
    //         send an influx event (but not right now)
    //     }
    //    }
}
